import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { callGet } from '@/api/fetcher';
import { BrainLanguage } from '@/shared/types/brain';
import {
  STTModelInfo,
  STTModelSettings,
  STTProvider,
  STTProviderInfo,
  STTProviderInternal,
  TTSModelInfo,
  TTSProvider,
  TTSProviderInfo,
  TTSVoiceInfo,
} from '@/shared/types/voiceProviders';

/**
 * Orchestrator Voice Providers API Types
 * Based on GET /v1/integrations/voice/providers response format
 */
interface OrchestratorSTTModel {
  id: string;
  name: string;
  /** Languages array is omitted when model supports all languages */
  languages?: string[];
}

interface OrchestratorSTTProvider {
  id: string;
  name: string;
  models: OrchestratorSTTModel[];
}

/**
 * Orchestrator TTS Types
 */
interface OrchestratorTTSVoice {
  id: string;
  name: string;
  description?: string;
  language?: string;
  category?: string;
  labels?: { accent?: string; language?: string };
  preview_url?: string;
}

interface OrchestratorTTSModel {
  id: string;
  name: string;
  languages?: string[];
}

interface OrchestratorTTSProvider {
  id: string;
  name: string;
  voices: OrchestratorTTSVoice[];
  models: OrchestratorTTSModel[];
}

/** @visibleForTesting */
export interface OrchestratorVoiceProvidersResponse {
  stt: { providers: OrchestratorSTTProvider[] };
  tts?: { providers: OrchestratorTTSProvider[] };
}

/**
 * Maps orchestrator provider IDs to our internal provider IDs
 */
const ORCHESTRATOR_PROVIDER_ID_MAP: Record<string, STTProviderInternal> = {
  deepgram: 'deepgram',
  elevenlabs: 'elevenlabs',
  soniox: 'soniox',
};

/**
 * STT Provider Configuration
 * Static configuration for Speech-to-Text providers
 */
const STT_PROVIDERS: Record<STTProviderInternal, STTProviderInfo> = {
  deepgram: { id: 'deepgram', name: 'Deepgram' },
  elevenlabs: { id: 'elevenlabs', name: 'ElevenLabs' },
  soniox: { id: 'soniox', name: 'Soniox' },
};

/**
 * Maps orchestrator TTS provider IDs to our internal provider IDs
 */
const ORCHESTRATOR_TTS_PROVIDER_ID_MAP: Record<string, TTSProvider> = {
  elevenlabs: 'elevenlabs',
  cartesia: 'cartesia',
  minimax: 'minimax',
};

/**
 * TTS Provider Configuration
 * Static configuration for Text-to-Speech providers
 */
const TTS_PROVIDERS: Record<TTSProvider, TTSProviderInfo> = {
  elevenlabs: { id: 'elevenlabs', name: 'ElevenLabs' },
  cartesia: { id: 'cartesia', name: 'Cartesia' },
  minimax: { id: 'minimax', name: 'MiniMax' },
};

// Deepgram Flux model settings
// Schema validation: eot_threshold (0.5-0.9), eot_timeout_ms (500-10000)
const DEEPGRAM_FLUX_SETTINGS: STTModelSettings[] = [
  {
    key: 'keyterm',
    label: 'Keyterms',
    type: 'multi-select',
    description: 'Key terms to boost recognition',
    translationKey: 'integrations.voice_common.transcription.keyterm',
  },
  {
    key: 'eot_threshold',
    label: 'End-of-Turn Threshold',
    type: 'slider',
    min: 0.5,
    max: 0.9,
    step: 0.01,
    default: 0.7,
    description: 'Sensitivity for detecting end of speech turn',
    translationKey: 'integrations.voice_common.transcription.eot_threshold',
  },
  {
    key: 'eot_timeout_ms',
    label: 'End-of-Turn Timeout',
    type: 'number',
    default: 5000,
    min: 500,
    max: 10000,
    description: 'Timeout for end-of-turn detection (ms)',
    translationKey: 'integrations.voice_common.transcription.eot_timeout_ms',
  },
];

// Deepgram Nova-2 model settings
// Schema validation: endpointing (0-5000), utterance_end_ms (1000-5000)
const DEEPGRAM_NOVA2_SETTINGS: STTModelSettings[] = [
  {
    key: 'keywords',
    label: 'Keywords',
    type: 'multi-select',
    description: 'Words to boost recognition',
    translationKey: 'integrations.voice_common.transcription.keywords',
  },
  {
    key: 'endpointing',
    label: 'Endpointing',
    type: 'number',
    default: 500,
    min: 0,
    max: 5000,
    description: 'Silence duration before finalizing utterance (ms)',
    translationKey: 'integrations.voice_common.transcription.endpointing',
  },
  {
    key: 'utterance_end_ms',
    label: 'Utterance End Timeout',
    type: 'number',
    default: 1000,
    min: 1000,
    max: 5000,
    description: 'Silence duration to finalize utterance (ms)',
    translationKey: 'integrations.voice_common.transcription.utterance_end_ms',
  },
  {
    key: 'smart_format',
    label: 'Smart Format',
    type: 'boolean',
    default: false,
    translationKey: 'integrations.voice_common.transcription.smart_format',
  },
  {
    key: 'numerals',
    label: 'Numerals',
    type: 'boolean',
    default: false,
    translationKey: 'integrations.voice_common.transcription.numerals',
  },
];

// Deepgram Nova-3 model settings
// Schema validation: endpointing (0-5000), utterance_end_ms (1000-5000)
const DEEPGRAM_NOVA3_SETTINGS: STTModelSettings[] = [
  {
    key: 'keyterm',
    label: 'Keyterms',
    type: 'multi-select',
    description: 'Key terms to boost recognition',
    translationKey: 'integrations.voice_common.transcription.keyterm',
  },
  {
    key: 'endpointing',
    label: 'Endpointing',
    type: 'number',
    default: 500,
    min: 0,
    max: 5000,
    description: 'Silence duration before finalizing utterance (ms)',
    translationKey: 'integrations.voice_common.transcription.endpointing',
  },
  {
    key: 'utterance_end_ms',
    label: 'Utterance End Timeout',
    type: 'number',
    default: 1000,
    min: 1000,
    max: 5000,
    description: 'Silence duration to finalize utterance (ms)',
    translationKey: 'integrations.voice_common.transcription.utterance_end_ms',
  },
  {
    key: 'smart_format',
    label: 'Smart Format',
    type: 'boolean',
    default: false,
    translationKey: 'integrations.voice_common.transcription.smart_format',
  },
  {
    key: 'numerals',
    label: 'Numerals',
    type: 'boolean',
    default: false,
    translationKey: 'integrations.voice_common.transcription.numerals',
  },
];

// ElevenLabs Scribe v2 Realtime model settings
// Schema validation: vad_silence_threshold_secs (0-10), vad_threshold (0-1)
const ELEVENLABS_SCRIBE_SETTINGS: STTModelSettings[] = [
  {
    key: 'vad_silence_threshold_secs',
    label: 'VAD Silence Threshold',
    type: 'number',
    min: 0.3,
    max: 3,
    step: 0.1,
    default: 1,
    description: 'Silence duration before auto-commit',
    translationKey: 'integrations.voice_common.transcription.vad_silence_threshold_secs',
  },
  {
    key: 'vad_threshold',
    label: 'VAD Threshold',
    type: 'slider',
    min: 0.1,
    max: 0.9,
    step: 0.01,
    default: 0.4,
    description: 'VAD detection sensitivity',
    translationKey: 'integrations.voice_common.transcription.vad_threshold',
  },
  {
    key: 'min_speech_duration_ms',
    label: 'Min Speech Duration',
    type: 'number',
    min: 50,
    max: 2000,
    step: 10,
    default: 100,
    description: 'Minimum speech duration before commit (ms)',
    translationKey: 'integrations.voice_common.transcription.min_speech_duration_ms',
  },
  {
    key: 'min_silence_duration_ms',
    label: 'Min Silence Duration',
    type: 'number',
    min: 50,
    max: 2000,
    step: 10,
    default: 100,
    description: 'Minimum silence duration before commit (ms)',
    translationKey: 'integrations.voice_common.transcription.min_silence_duration_ms',
  },
];

// Soniox Realtime model settings
// Schema validation: max_endpoint_delay_ms (500-3000)
const SONIOX_SETTINGS: STTModelSettings[] = [
  {
    key: 'max_endpoint_delay_ms',
    label: 'Max Endpoint Delay',
    type: 'number',
    default: 2000,
    min: 500,
    max: 3000,
    description: 'Maximum delay before endpoint detection (ms)',
    translationKey: 'integrations.voice_common.transcription.max_endpoint_delay_ms',
  },
  {
    key: 'context.text',
    label: 'Context Text',
    type: 'text',
    multiline: true,
    description: 'Background context to improve transcription accuracy',
    translationKey: 'integrations.voice_common.transcription.context_text',
  },
  {
    key: 'context.terms',
    label: 'Context Terms',
    type: 'multi-select',
    description: 'Domain-specific terms for better recognition',
    translationKey: 'integrations.voice_common.transcription.context_terms',
  },
  {
    key: 'enable_speaker_diarization',
    label: 'Speaker Isolation',
    type: 'boolean',
    default: false,
    description: 'Transcribe only the primary speaker in the call',
    translationKey: 'integrations.voice_common.transcription.speaker_isolation',
  },
];

// stt-rt-v5 adds endpoint_sensitivity on top of the shared Soniox settings.
const SONIOX_V5_SETTINGS: STTModelSettings[] = [
  ...SONIOX_SETTINGS,
  {
    key: 'endpoint_sensitivity',
    label: 'Endpoint Sensitivity',
    type: 'slider',
    min: -1,
    max: 1,
    step: 0.05,
    default: -0.5,
    description: 'Higher finalizes speech sooner; lower waits longer',
    translationKey: 'integrations.voice_common.transcription.endpoint_sensitivity',
  },
];

/**
 * Model settings map - maps model IDs to their UI settings configuration
 */
const STT_MODEL_SETTINGS: Record<string, STTModelSettings[]> = {
  // Deepgram models have different settings per model
  'flux-general-en': DEEPGRAM_FLUX_SETTINGS,
  'flux-general-multi': DEEPGRAM_FLUX_SETTINGS,
  'nova-3': DEEPGRAM_NOVA3_SETTINGS,
  'nova-3-medical': DEEPGRAM_NOVA3_SETTINGS,
  'nova-2': DEEPGRAM_NOVA2_SETTINGS,
  // ElevenLabs models
  scribe_v2_realtime: ELEVENLABS_SCRIBE_SETTINGS,
  // Soniox models
  'stt-rt-v4': SONIOX_SETTINGS,
  'stt-rt-v5': SONIOX_V5_SETTINGS,
};

export const VOICE_PROVIDERS_ENDPOINT = '/www/api/v1/integrations/voice/providers';

interface STTProvidersResponse {
  providers: Record<STTProviderInternal, STTProviderInfo>;
  models: STTModelInfo[];
}

/**
 * Transforms orchestrator response into our STTProvidersResponse format
 * Merges dynamic data from orchestrator with local settings configuration
 * @visibleForTesting
 */
export const transformOrchestratorResponse = (
  orchestratorData: OrchestratorVoiceProvidersResponse
): STTProvidersResponse => {
  const providers: Record<STTProviderInternal, STTProviderInfo> = {} as Record<
    STTProviderInternal,
    STTProviderInfo
  >;
  const models: STTModelInfo[] = [];

  if (!orchestratorData?.stt?.providers) {
    return { providers, models };
  }

  for (const orchestratorProvider of orchestratorData.stt.providers) {
    // Map orchestrator provider ID to our internal ID
    const internalProviderId = ORCHESTRATOR_PROVIDER_ID_MAP[orchestratorProvider.id];

    if (!internalProviderId) {
      // Skip unknown providers
      continue;
    }

    // Use local provider info if available, otherwise create from orchestrator data
    providers[internalProviderId] = STT_PROVIDERS[internalProviderId] || {
      id: internalProviderId,
      name: orchestratorProvider.name,
    };

    // Transform models
    for (const orchestratorModel of orchestratorProvider.models) {
      const modelInfo: STTModelInfo = {
        id: orchestratorModel.id,
        name: orchestratorModel.name,
        provider: internalProviderId,
        // If languages is omitted, it means all languages are supported
        supportedLanguages: orchestratorModel.languages || Object.values(BrainLanguage),
        // Get settings from local config, or empty array if not defined
        settings: STT_MODEL_SETTINGS[orchestratorModel.id] || [],
      };

      models.push(modelInfo);
    }
  }

  return { providers, models };
};

/**
 * Hook for retrieving STT providers and models configuration
 * Fetches dynamic data from orchestrator and merges with local settings
 * @returns Object containing providers, models, helper functions, and loading state
 */
export const useVoiceSTT = (speech_model_id?: string) => {
  const endpoint = speech_model_id
    ? `${VOICE_PROVIDERS_ENDPOINT}?speech_model_id=${speech_model_id}`
    : VOICE_PROVIDERS_ENDPOINT;
  const { data, isLoading, status, refetch } = useQuery<OrchestratorVoiceProvidersResponse>({
    queryKey: [VOICE_PROVIDERS_ENDPOINT, speech_model_id],
    queryFn: () => callGet(endpoint),
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes (static config)
  });

  // Transform orchestrator response to our format
  const transformedData = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return transformOrchestratorResponse(data);
  }, [data]);

  const providers = transformedData?.providers;
  const models = transformedData?.models;

  // Helper function to get models by provider
  const getModelsByProvider = useMemo(() => {
    return (provider: STTProvider): STTModelInfo[] => {
      return models?.filter((m) => m.provider === provider) ?? [];
    };
  }, [models]);

  const getModelById = useMemo(() => {
    return (modelId: string): STTModelInfo | undefined => {
      return models?.find((m) => m.id === modelId);
    };
  }, [models]);

  return { providers, models, isLoading, status, refetch, getModelsByProvider, getModelById };
};

/**
 * TTS Providers Response Type
 */
interface TTSProvidersResponse {
  providers: Record<TTSProvider, TTSProviderInfo>;
  models: TTSModelInfo[];
  voices: TTSVoiceInfo[];
}

/**
 * Transforms orchestrator TTS response into our TTSProvidersResponse format
 * @visibleForTesting
 */
export const transformTTSOrchestratorResponse = (
  orchestratorData: OrchestratorVoiceProvidersResponse
): TTSProvidersResponse | undefined => {
  if (!orchestratorData?.tts?.providers) {
    return undefined;
  }

  const providers: Record<TTSProvider, TTSProviderInfo> = {} as Record<
    TTSProvider,
    TTSProviderInfo
  >;
  const models: TTSModelInfo[] = [];
  const voices: TTSVoiceInfo[] = [];

  for (const orchestratorProvider of orchestratorData.tts.providers) {
    const internalProviderId = ORCHESTRATOR_TTS_PROVIDER_ID_MAP[orchestratorProvider.id];

    if (!internalProviderId) {
      continue;
    }

    providers[internalProviderId] = TTS_PROVIDERS[internalProviderId] || {
      id: internalProviderId,
      name: orchestratorProvider.name,
    };

    // Transform models
    for (const orchestratorModel of orchestratorProvider.models) {
      models.push({
        id: orchestratorModel.id,
        name: orchestratorModel.name,
        provider: internalProviderId,
        languages: orchestratorModel.languages,
      });
    }

    // Transform voices
    for (const orchestratorVoice of orchestratorProvider.voices) {
      const labelLanguage = orchestratorVoice.labels?.language;
      const topLevelLanguage = orchestratorVoice.language;
      const accent =
        !labelLanguage || !topLevelLanguage || labelLanguage === topLevelLanguage
          ? orchestratorVoice.labels?.accent
          : undefined;

      voices.push({
        id: orchestratorVoice.id,
        name: orchestratorVoice.name,
        description: orchestratorVoice.description,
        language: topLevelLanguage || labelLanguage,
        provider: internalProviderId,
        accent,
        previewUrl: orchestratorVoice.preview_url,
      });
    }
  }

  return { providers, models, voices };
};

/**
 * Hook for retrieving TTS providers, models, and voices configuration
 * Fetches dynamic data from orchestrator
 * @returns Object containing providers, models, voices, helper functions, and loading state
 */
export const useVoiceTTS = (integration_id?: string, speech_model_id?: string) => {
  let endpoint = VOICE_PROVIDERS_ENDPOINT;
  if (integration_id) {
    endpoint = `${VOICE_PROVIDERS_ENDPOINT}?integration_id=${integration_id}`;
  } else if (speech_model_id) {
    endpoint = `${VOICE_PROVIDERS_ENDPOINT}?speech_model_id=${speech_model_id}`;
  }

  const { data, isLoading, status, refetch } = useQuery<OrchestratorVoiceProvidersResponse>({
    queryKey: [VOICE_PROVIDERS_ENDPOINT, integration_id ?? speech_model_id],
    queryFn: () => callGet(endpoint),
    staleTime: 10 * 60 * 1000,
  });

  // Transform orchestrator response to our TTS format
  const transformedData = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return transformTTSOrchestratorResponse(data);
  }, [data]);

  const providers = transformedData?.providers;
  const models = transformedData?.models;
  const voices = transformedData?.voices;

  const getModelsByProvider = useMemo(() => {
    return (provider: TTSProvider): TTSModelInfo[] => {
      return models?.filter((m) => m.provider === provider) ?? [];
    };
  }, [models]);

  const getModelsByProviderAndLanguage = useMemo(() => {
    return (provider: TTSProvider, language?: string): TTSModelInfo[] => {
      let filtered = models?.filter((m) => m.provider === provider) ?? [];
      if (language) {
        filtered = filtered.filter((m) => !m.languages || m.languages.includes(language));
      }
      return filtered;
    };
  }, [models]);

  const getVoicesByProvider = useMemo(() => {
    return (provider: TTSProvider, language?: string): TTSVoiceInfo[] => {
      let filteredVoices = voices?.filter((v) => v.provider === provider) ?? [];
      if (language) {
        filteredVoices = filteredVoices.filter((v) => !v.language || v.language === language);
      }
      return filteredVoices;
    };
  }, [voices]);

  const getModelById = useMemo(() => {
    return (modelId: string): TTSModelInfo | undefined => {
      return models?.find((m) => m.id === modelId);
    };
  }, [models]);

  return {
    providers,
    models,
    voices,
    isLoading,
    status,
    refetch,
    getModelsByProvider,
    getModelsByProviderAndLanguage,
    getVoicesByProvider,
    getModelById,
  };
};
